/**
 * PaymentMethods Page - Card Layout
 * Refactored with Clean Architecture pattern
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, CreditCard, Edit, Trash2, Banknote, QrCode, Wallet, Loader2 } from 'lucide-react';

import PaymentMethodModal from './PaymentMethodModal';
import './styles.css';

const iconMap = { Banknote, CreditCard, QrCode, Wallet };

export default function PaymentMethods() {
  const { t } = useTranslation();
  
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/payment-methods');
      setMethods(res.data);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingMethod?.id) {
        await api.put(`/payment-methods/${editingMethod.id}`, data);
      } else {
        await api.post('/payment-methods', data);
      }
      toast.success('Forma de pagamento salva com sucesso');
      setModalOpen(false);
      setEditingMethod(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingMethod to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingMethod(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/payment-methods/${deleteId}`);
      toast.success('Forma de pagamento excluída com sucesso');
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="payment-methods-page" data-testid="payment-methods-page">
      <div className="payment-methods-header">
        <div className="payment-methods-header-title">
          <h1>
            <CreditCard className="h-6 w-6" />
            {t('nav.paymentMethods')}
          </h1>
          <p>{methods.length} formas de pagamento cadastradas</p>
        </div>
        <Button 
          onClick={() => { setEditingMethod(null); setModalOpen(true); }}
          data-testid="new-payment-method-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Forma
        </Button>
      </div>

      <div className="payment-methods-grid">
        {methods.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              {t('common.noResults')}
            </CardContent>
          </Card>
        ) : (
          methods.map(method => {
            const Icon = iconMap[method.icon] || CreditCard;
            return (
              <Card key={method.id} className="payment-method-card" data-testid={`payment-method-card-${method.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="payment-method-icon">
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant={method.is_active ? 'default' : 'secondary'}>
                      {method.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{method.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Ícone: {method.icon}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setEditingMethod(method);
                        setModalOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(method.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <PaymentMethodModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        paymentMethod={editingMethod}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir esta forma de pagamento?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
